import { useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentToken, globalLogout, setCredentials, selectIsRedirectionRequired } from './authSlice';
import Layout from '../../components/Layout/layout';
import { useEffect, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import { Button } from 'react-bootstrap';
import Timer from '../../components/Timer';
import { useNavigate } from 'react-router-dom';
import { useLogoutMutation, useRefreshMutation } from './authApiSlice';
import { useIdleTimer } from 'react-idle-timer';

const RequireAuth = () => {
  const token = useSelector(selectCurrentToken);
  const isRedirectionRequired = useSelector(selectIsRedirectionRequired);
  const [refresh] = useRefreshMutation();
  const [logout] = useLogoutMutation();
  const location = useLocation();
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(120);
  const [logoutCountdownActive, setLogoutCountdownActive] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tokenExpiration = useSelector(state => state.auth.accessTokenExpiration);
  const tokenExpirationRef = useRef(tokenExpiration);
  const updateRefreshTokenTimerRef = useRef(null);
  const idleStartTime = useRef(null);
  const idleTimeoutInMinutes = 30;

  const handleOnIdle = () => {
    setShowIdleModal(true);
    setLogoutCountdownActive(true);
    idleStartTime.current = new Date();
  };

  const { pause: stopIdleTimer } = useIdleTimer({
    timeout: 60000 * idleTimeoutInMinutes,
    onIdle: handleOnIdle,
    startOnMount: true,
    name: 'idleTimer',
  });

  const updateAccessToken = async () => {
    if (secondsUntilLogout > 0) {
      try {
        await refresh()
          .unwrap()
          .then((authResponse) => {
            dispatch(
              setCredentials({
                accessToken: authResponse.accessToken
              })
            );
          });
      } catch (ex) {
        console.log(ex);
      }
    }
  };

  useEffect(() => {
    tokenExpirationRef.current = tokenExpiration;
  }, [tokenExpiration]);

  useEffect(() => {
    updateRefreshTokenTimerRef.current = setInterval(updateAccessToken, Math.abs(new Date(tokenExpirationRef.current) - new Date()) + 1000);

    return () => {
      clearInterval(updateRefreshTokenTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (secondsUntilLogout <= 0) {
      clearInterval(updateRefreshTokenTimerRef.current);
      handleLogOut(false);
      stopIdleTimer();
    }
  }, [secondsUntilLogout]);

  const handleCloseIdleModal = async () => {

    if (secondsUntilLogout <= 0) {
      handleLogOut(true);
    } else {
      setShowIdleModal(false);
      setLogoutCountdownActive(false);
      setSecondsUntilLogout(120);
    }
  };

  const handleLogOut = async (redirectToLogin) => {
    try {
      var response = await logout().unwrap();
      if (response.isSuccess) {
        dispatch(globalLogout());
        if (redirectToLogin && response.redirectUrl && response.redirectUrl.length > 0) {
          window.location.href = response.redirectUrl;
          return;
        }
        if (redirectToLogin) navigate('/login');
      }
    } catch (ex) {
      console.log(ex);
      dispatch(globalLogout());
      if (redirectToLogin) navigate('/login');
    }
  };

  return token ? <>
    {isRedirectionRequired?<>Redirecting...</>:<Layout />}

    <Modal
      isOpen={showIdleModal}
      onClose={() => handleCloseIdleModal()}
      contentWidth="700px"
      titleName='Are you still there?'>

      <>
        {secondsUntilLogout > 0 && <div className='d-flex flex-column gap-3'>
          <span>Your session is about to expire and you will be logged out in <Timer type='countdown'
            timerSeconds={secondsUntilLogout}
            setTimerSeconds={setSecondsUntilLogout}
            timerActive={logoutCountdownActive}
            setTimerActive={setLogoutCountdownActive}
            stringFormat={'mm:ss'} /> minutes.
          </span>

          <div className='d-flex flex-row gap-2 justify-content-end'>
            <Button variant='castleGray' onClick={() => handleLogOut(true)}>Log Out Now</Button>
            <Button variant='castleOrange' onClick={() => handleCloseIdleModal()}>Continue Session</Button>
          </div>
        </div>
        }

        {secondsUntilLogout <= 0 && <div className='d-flex flex-column gap-3'>
          <span>We&apos;ve noticed that you haven&apos;t been active in awhile, so we logged you out for security reasons.  Please click the button to login again.</span>
          <div className='d-flex flex-row gap-2 justify-content-end'>
            <Button variant='castleOrange' onClick={() => handleLogOut(true)}>Login</Button>
          </div>
        </div>
        }
      </>
    </Modal>

  </> : <Navigate to="/login" state={{ from: location }} replace />;
};

export default RequireAuth;
